import type { OrderStatus } from '@telestore/contracts';
import { formatMoney, type Currency } from '@telestore/shared';
import { AlertTriangle, Check, ChevronRight, Copy, Inbox, Loader2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { Badge, type BadgeTone } from './ui/badge.js';
import { Button } from './ui/button.js';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card.js';
import { Label } from './ui/input.js';

/**
 * Admin primitives, now built on the shadcn layer in `./ui/` (§7 sanctions
 * shadcn here "for form/table velocity").
 *
 * The exported API is unchanged on purpose. Eight pages call these, and a
 * rename-everything migration would have been a large diff whose only visible
 * result is the same screens — so shadcn went underneath instead, and the call
 * sites got icons.
 *
 * Semantic, not decorative: a merchant scanning an order list needs "this one
 * needs me" to be visible without reading.
 */

export function Money({
  amount,
  currency,
  className,
}: {
  amount: number;
  currency: Currency;
  className?: string;
}) {
  return <span className={className}>{formatMoney({ amount, currency })}</span>;
}

type Tone = BadgeTone;

export function Pill({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return <Badge tone={tone}>{children}</Badge>;
}

/**
 * Status colouring encodes urgency, not category: amber means the merchant has
 * to do something, green means money has arrived, grey means waiting on
 * somebody else.
 */
export const ORDER_STATUS_TONE: Record<OrderStatus, Tone> = {
  draft: 'neutral',
  pending_confirm: 'warn',
  awaiting_payment: 'neutral',
  paid: 'positive',
  fulfilling: 'brand',
  confirmed: 'brand',
  shipped: 'brand',
  collected: 'positive',
  remitted: 'positive',
  // A booked appointment is settled but not yet done — the same "waiting on
  // somebody else" grey as a shipped parcel, not an action the merchant owes.
  scheduled: 'brand',
  // Amber: a no-show is money the merchant may need to decide about.
  no_show: 'warn',
  completed: 'neutral',
  cancelled: 'danger',
  refunded: 'danger',
};

export function Panel({
  title,
  action,
  children,
  padded = true,
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  padded?: boolean;
}) {
  return (
    <Card>
      {title ? (
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {action}
        </CardHeader>
      ) : null}
      {padded ? <CardContent>{children}</CardContent> : children}
    </Card>
  );
}

export function Stat({
  label,
  value,
  hint,
  tone = 'neutral',
  icon,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: Tone;
  /** A lucide icon, when one adds meaning the number alone does not carry. */
  icon?: ReactNode;
}) {
  const colour =
    tone === 'positive'
      ? 'var(--color-positive)'
      : tone === 'warn'
        ? 'var(--color-warn)'
        : tone === 'danger'
          ? 'var(--color-danger)'
          : tone === 'brand'
            ? 'var(--color-brand)'
            : 'var(--color-ink)';

  return (
    <Card className="p-4">
      <div className="flex items-center gap-1.5">
        {icon ? <span className="text-muted-foreground">{icon}</span> : null}
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
      </div>
      <p className="mt-1 text-2xl font-bold tabular-nums" style={{ color: colour }}>
        {value}
      </p>
      {hint ? <p className="mt-0.5 text-xs text-[var(--color-ink-faint)]">{hint}</p> : null}
    </Card>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <Label asChild>
        <span>{label}</span>
      </Label>
      {children}
      {hint && !error ? (
        <span className="mt-1 block text-xs text-[var(--color-ink-faint)]">{hint}</span>
      ) : null}
      {error ? (
        <span className="mt-1 flex items-center gap-1 text-xs text-destructive">
          <AlertTriangle className="size-3" aria-hidden />
          {error}
        </span>
      ) : null}
    </label>
  );
}

/**
 * `Field`, but for a group of radios.
 *
 * `Field` is a `<label>`, and a label may not contain other labels — which is
 * what a radio group is. Nested labels are invalid HTML and make the whole
 * group ambiguous to a screen reader: it has to guess which control the outer
 * label names. `<fieldset>`/`<legend>` is the element pair that exists for
 * exactly this, so the legend names the QUESTION and each radio keeps its own
 * label for its answer.
 */
export function Fieldset({
  legend,
  hint,
  children,
}: {
  legend: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <fieldset className="block border-0 p-0">
      <Label asChild>
        <legend className="mb-1">{legend}</legend>
      </Label>
      {children}
      {hint ? (
        <span className="mt-1 block text-xs text-[var(--color-ink-faint)]">{hint}</span>
      ) : null}
    </fieldset>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-10" role="status" aria-live="polite">
      <Loader2 className="size-5 animate-spin text-primary" aria-hidden />
      {label ? <span className="text-sm text-muted-foreground">{label}</span> : null}
    </div>
  );
}

export function Empty({
  title,
  hint,
  action,
  icon,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-14 text-center">
      <span className="text-[var(--color-ink-faint)]" aria-hidden>
        {icon ?? <Inbox className="size-7" />}
      </span>
      <p className="font-semibold">{title}</p>
      {hint ? <p className="max-w-sm text-sm text-muted-foreground">{hint}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export function ErrorBox({
  message,
  onRetry,
  retryLabel,
}: {
  message: string;
  onRetry?: () => void;
  retryLabel?: string;
}) {
  return (
    <Card
      className="border-destructive bg-[var(--color-danger-soft)] p-4"
      role="alert"
    >
      <p className="flex items-start gap-2 text-sm text-destructive">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
        {message}
      </p>
      {onRetry ? (
        <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
          {retryLabel ?? 'Retry'}
        </Button>
      ) : null}
    </Card>
  );
}

/**
 * Lists are the admin's main surface and they are used on a phone, so they
 * render as stacked rows rather than a table that needs horizontal scrolling.
 */
export function Rows({ children }: { children: ReactNode }) {
  return <div className="flex flex-col">{children}</div>;
}

export function Row({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  const base = 'flex items-center gap-3 border-b border-border px-4 py-3 text-left';
  if (!onClick) return <div className={base}>{children}</div>;

  return (
    <button type="button" className={cn(base, 'w-full hover:bg-muted')} onClick={onClick}>
      {children}
      {/* A tappable row has to look tappable; the chevron is the only cue a
          stacked list gets. */}
      <ChevronRight className="size-4 shrink-0 text-[var(--color-ink-faint)]" aria-hidden />
    </button>
  );
}

/**
 * Copy to clipboard.
 *
 * State rather than mutating `textContent` — the previous version wrote into
 * the DOM node directly, which React then owned a stale idea of, and a
 * re-render mid-timeout left the button stuck reading "Copied".
 */
export function CopyButton({
  value,
  label,
  copiedLabel,
}: {
  value: string;
  label: string;
  copiedLabel: string;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1400);
        });
      }}
    >
      {copied ? <Check className="text-[var(--color-positive)]" /> : <Copy />}
      {copied ? copiedLabel : label}
    </Button>
  );
}

export { Button } from './ui/button.js';
export { Input, Select, Textarea } from './ui/input.js';
